import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

const ServicesPage = () => {
  // SEO Configuration
  useSEO({
    title: 'IT Services Warrington - Software Development, AI, Cloud & Cybersecurity',
    description: 'PURECODE Ltd offers comprehensive IT services in Warrington: custom software development, AI & machine learning, cloud infrastructure, cybersecurity, data analytics, and digital transformation for banking and fintech businesses.',
    keywords: 'IT services Warrington, software development, AI solutions, cloud services, cybersecurity Warrington, fintech development, digital transformation',
    canonical: 'https://purecode.uk/services',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' }
        ]),
        {
          '@type': 'ItemList',
          itemListElement: [
            {
              '@type': 'Service',
              name: 'Custom Software Development',
              description: 'Tailored software solutions for financial services, fintech startups, and betting platforms',
              provider: { '@id': 'https://purecode.uk/#organization' }
            },
            {
              '@type': 'Service',
              name: 'AI & Machine Learning Solutions',
              description: 'Smart AI solutions for fraud detection, customer analytics, and automated systems',
              provider: { '@id': 'https://purecode.uk/#organization' }
            },
            {
              '@type': 'Service',
              name: 'Cloud Solutions & Infrastructure',
              description: 'Secure, scalable cloud solutions with 99.5% uptime',
              provider: { '@id': 'https://purecode.uk/#organization' }
            },
            {
              '@type': 'Service',
              name: 'Cybersecurity Services',
              description: 'Security frameworks, compliance assistance, and risk management',
              provider: { '@id': 'https://purecode.uk/#organization' }
            },
            {
              '@type': 'Service',
              name: 'Data Analytics & Business Intelligence',
              description: 'Analytics platforms for business insights and operational reporting',
              provider: { '@id': 'https://purecode.uk/#organization' }
            },
            {
              '@type': 'Service',
              name: 'Digital Transformation',
              description: 'Digital transformation consulting for businesses moving into fintech',
              provider: { '@id': 'https://purecode.uk/#organization' }
            }
          ]
        }
      ]
    }
  });

  const services = [
    {
      icon: 'ri-code-s-slash-line',
      title: 'Custom Software Development',
      description: 'Tailored software solutions for small to medium financial services, fintech startups, and local betting platforms.',
      features: [
        'Web Application Development',
        'Mobile App Development',
        'Desktop Applications',
        'API Development & Integration',
        'Legacy System Modernization'
      ],
      image: 'https://public.readdy.ai/ai/img_res/24c07d5821777d5b4dbd65e7254ba57e.jpg'
    },
    {
      icon: 'ri-brain-line',
      title: 'AI & Machine Learning Solutions',
      description: 'Smart AI solutions for fraud detection, customer analytics, and automated systems for growing businesses.',
      features: [
        'Machine Learning Model Development',
        'Natural Language Processing',
        'Computer Vision Solutions',
        'Predictive Analytics',
        'AI-Powered Automation'
      ],
      image: 'https://readdy.ai/api/search-image?query=AI%20and%20machine%20learning%20visualization%20with%20neural%20networks%2C%20data%20flowing%20through%20digital%20brain%20patterns%2C%20futuristic%20technology%20interface%20with%20teal%20and%20cyan%20colors%2C%20artificial%20intelligence%20concept%20art&width=600&height=400&seq=service2&orientation=landscape'
    },
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Solutions & Infrastructure',
      description: 'Secure, scalable cloud solutions with 99.5% uptime for financial services and emerging fintech companies.',
      features: [
        'Cloud Migration Services',
        'Infrastructure as Code',
        'DevOps & CI/CD Implementation',
        'Cloud Security & Compliance',
        'Performance Optimization'
      ],
      image: 'https://readdy.ai/api/search-image?query=Cloud%20computing%20infrastructure%20with%20servers%20and%20data%20centers%2C%20network%20connections%20visualized%20in%20teal%20light%2C%20modern%20technology%20architecture%2C%20professional%20cloud%20services%20environment&width=600&height=400&seq=service3&orientation=landscape'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Cybersecurity Services',
      description: 'Security frameworks, compliance assistance, and risk management systems for regulated businesses.',
      features: [
        'Security Audits & Assessments',
        'Penetration Testing',
        'Compliance Management',
        'Incident Response Planning',
        'Security Training & Awareness'
      ],
      image: 'https://readdy.ai/api/search-image?query=Cybersecurity%20concept%20with%20digital%20shield%20protecting%20data%2C%20network%20security%20visualization%20with%20encrypted%20connections%2C%20professional%20security%20operations%20center%20with%20monitoring%20screens&width=600&height=400&seq=service4&orientation=landscape'
    },
    {
      icon: 'ri-database-2-line',
      title: 'Data Analytics & Business Intelligence',
      description: 'Analytics platforms for business insights, customer behaviour analysis, and operational reporting.',
      features: [
        'Data Warehouse Design',
        'Business Intelligence Dashboards',
        'Real-time Analytics',
        'Data Visualization',
        'Performance Metrics & KPIs'
      ],
      image: 'https://readdy.ai/api/search-image?query=Business%20intelligence%20dashboard%20with%20charts%20and%20graphs%2C%20data%20analytics%20visualization%20on%20large%20screens%2C%20professional%20business%20analyst%20working%20with%20data%20insights%2C%20modern%20analytics%20workspace&width=600&height=400&seq=service5&orientation=landscape'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Digital Transformation',
      description: 'Digital transformation consulting for traditional businesses moving into the fintech space.',
      features: [
        'Technology Strategy Planning',
        'Digital Transformation Roadmaps',
        'IT Architecture Design',
        'Vendor Selection & Management',
        'Technology ROI Analysis'
      ],
      image: 'https://readdy.ai/api/search-image?query=IT%20consultants%20presenting%20technology%20strategy%20to%20business%20executives%2C%20professional%20meeting%20room%20with%20presentation%20screens%2C%20strategic%20planning%20session%20with%20charts%20and%20diagrams&width=600&height=400&seq=service6&orientation=landscape'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Analysis',
      description: 'We start by understanding your business needs, challenges, and objectives through comprehensive analysis.'
    },
    {
      step: '02',
      title: 'Strategy & Planning',
      description: 'Our experts develop a customized strategy and detailed project plan tailored to your requirements.'
    },
    {
      step: '03',
      title: 'Development & Implementation',
      description: 'We execute the plan using agile methodologies, ensuring quality and timely delivery.'
    },
    {
      step: '04',
      title: 'Testing & Quality Assurance',
      description: 'Rigorous testing ensures your solution meets the highest standards of quality and performance.'
    },
    {
      step: '05',
      title: 'Deployment & Support',
      description: 'We handle deployment and provide ongoing support to ensure your solution continues to perform optimally.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 text-white py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.85), rgba(8, 145, 178, 0.85)), url('https://readdy.ai/api/search-image?query=Small%20modern%20tech%20office%20with%205%20developers%20working%20on%20computers%2C%20collaborative%20workspace%2C%20startup%20environment%2C%20financial%20technology%20development%2C%20warm%20lighting%20with%20teal%20tones&width=1920&height=600&seq=services-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-cyan-900/70 to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-6xl mb-6">
            Our Services
          </h1>
          <p className="text-xl leading-8 text-teal-100 max-w-3xl mx-auto">
            Boutique IT solutions designed to accelerate your digital transformation 
            and drive business growth through innovative fintech technology.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              What We Offer
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Specialized technology services for fintech startups and growing businesses
            </p>
          </div>
          
          <div className="space-y-16">
            {services.map((service, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl mb-6">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-2 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <i className="ri-check-line text-green-500 text-xl mr-3"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="inline-block rounded-lg bg-teal-600 px-6 py-3 text-lg font-semibold text-white shadow-lg hover:bg-teal-500 transform hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Learn More
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-lg shadow-xl object-cover w-full h-80"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gradient-to-r from-teal-50 to-cyan-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Our Process
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              A proven methodology that ensures successful project delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Technologies We Work With
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Modern tools and platforms to deliver exceptional results
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {[
              'Python', 'JavaScript', 'React', 'Node.js', 'AWS', 'Azure',
              'TensorFlow', 'Docker', 'Kubernetes', 'MongoDB', 'PostgreSQL', 'Redis'
            ].map((tech, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg text-center hover:bg-teal-50 transition-colors">
                <span className="text-gray-700 font-medium">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section 
        className="py-24 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.85), rgba(8, 145, 178, 0.85)), url('https://readdy.ai/api/search-image?query=Modern%20startup%20office%20environment%20with%20team%20collaboration%2C%20fintech%20development%20workspace%2C%20small%20business%20technology%20solutions%2C%20warm%20lighting%20with%20teal%20tones&width=1920&height=600&seq=services-cta&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-cyan-900/70 to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how our boutique services can help transform your business and achieve your technology goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 px-8 py-4 text-lg font-semibold text-white shadow-xl hover:from-teal-400 hover:to-cyan-400 transform hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Request Consultation
            </Link>
            <Link
              to="/case-studies"
              className="rounded-lg border-2 border-teal-300 px-8 py-4 text-lg font-semibold text-white hover:bg-teal-500/20 hover:border-teal-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
